import React, { useState } from 'react';
import {
	TableContainer,
	Paper,
	Table,
	TableHead,
	TableBody,
	TableRow,
	TableCell,
	Tooltip,
	Checkbox,
	TableSortLabel,
	useTheme,
} from '@mui/material';
import { toast } from 'react-toastify';
import CancelIcon from '@mui/icons-material/Cancel';
import Pagination from '@mui/material/Pagination';
import MonetizationOnIcon from '@mui/icons-material/MonetizationOn';
import ErrorRoundedIcon from '@mui/icons-material/ErrorRounded';
import UpdateIcon from '@mui/icons-material/Update';
import moment from 'moment';
import ORBApi from '../../../../services/ORBApi';

export default function PaymentFilesTable(props) {
	// {tableData, onWorksAlertClick}
	const theme = useTheme();
	const order = props.order || 'desc';
	const orderBy = props.orderBy || 'month';

	// Mapeamento entre headers e campos dos dados
	// IMPORTANTE: Os nomes aqui devem corresponder aos campos do backend
	const getFieldName = (headerTitle) => {
		const headerMap = {
			'Data': 'month',
			'Nome': 'name',
			'Fonte': 'source',
			'Status': 'status',
			'Total registros': 'total_rows',
			'Câmbio': null, // Não ordenável (array de rates)
			'Mostrar taxas de câmbio': null, // Não ordenável (checkbox)
			'Remover': null, // Não ordenável (botão)
		};
		return headerMap[headerTitle] || null;
	};

	// Handler para mudança de ordenação - comunica ao componente pai
	const handleRequestSort = (property) => {
		if (!props.onSortChange) return;
		
		const isAsc = orderBy === property && order === 'asc';
		const newOrder = isAsc ? 'desc' : 'asc';
		const newOrderBy = property;
		
		props.onSortChange(newOrderBy, newOrder);
	};

	const clickHandler = (report_id, showExchangeRate) => {
		props.setLoading(1);
		let body = { show_exchange_rate: showExchangeRate };
		ORBApi.updateWorkPaymentDetails(report_id, body)
			.then(() => {
				toast.success('Arquivo de pagamento atualizado');
				props.loadData(props.tableData.current_page);
			})
			.catch((error) => {
				toast.error('Erro ao atualizar arquivo de pagamento');
			})
			.finally(() => {
				props.setLoading(0);
			});
	};

	const buildRateTag = (report) => {
		return (
			<>
				{report.status === 'Loaded' &&
				props.hasRole(['PAYMENT_FILE_WORKS_WRITE']) ? (
					<MonetizationOnIcon
						color='secondary'
						className='clickable'
						onClick={() => {
							props.onRateClick(report);
						}}
					/>
				) : null}
				{report.rates.map((rate) => {
					if (rate !== 'BRL') {
						return (
							<div key={rate.currency}>
								{rate.currency}={rate.rate}
							</div>
						);
					}
					return null;
				})}
			</>
		);
	};
	return (
		<>
			<TableContainer className='table-container' component={Paper}>
				<Table
					style={{ borderRadius: '3%' }}
					size='small'
					className='basic-table '
				>
					<TableHead className='basic-table-head'>
						<TableRow>
							{['Data', 'Nome', 'Fonte', 'Status', 'Total registros', 'Câmbio', 'Mostrar taxas de câmbio', 'Remover', ''].map((title, idx) => {
								const fieldName = getFieldName(title);
								const isSortable = fieldName !== null;
								
								return (
									<TableCell
										key={idx}
										align={title === 'Total registros' || title === 'Mostrar taxas de câmbio' || title === 'Remover' ? 'center' : 'left'}
										style={{ color: '#F5F5F5' }}
										sortDirection={orderBy === fieldName ? order : false}
									>
										{isSortable ? (
											<TableSortLabel
												active={orderBy === fieldName}
												direction={orderBy === fieldName ? order : 'asc'}
												onClick={() => handleRequestSort(fieldName)}
												sx={{
													'&.Mui-active': {
														color: theme.palette.secondary.main,
													},
													'& .MuiTableSortLabel-icon': {
														color: orderBy === fieldName 
															? `${theme.palette.secondary.main} !important` 
															: 'rgba(255, 255, 255, 0.54)',
													},
													'&:hover': {
														color: theme.palette.secondary.light,
													},
													'&:hover .MuiTableSortLabel-icon': {
														color: `${theme.palette.secondary.light} !important`,
													},
													color: orderBy === fieldName 
														? theme.palette.secondary.main 
														: 'inherit',
												}}
											>
												{title}
											</TableSortLabel>
										) : (
											title
										)}
									</TableCell>
								);
							})}
						</TableRow>
					</TableHead>
					<TableBody>
					{props.tableData?.work_payment_files?.length > 0 &&
					props.hasRole([
						'PAYMENT_FILE_WORKS_WRITE',
						'PAYMENT_FILE_WORKS_READ',
					])
						? props.tableData.work_payment_files.map(
									(report, index) => {
										return (
											<TableRow
												className='row'
												key={report.id}
											>
												<TableCell
													style={{ width: '10%' }}
												>
													{moment(
														report.month
													).format('MM/YYYY')}
												</TableCell>
												<TableCell
													style={{ width: '40%' }}
												>
													{report.name}
													{report.has_not_found_works ? (
														<div
															className='alert-notfound-works clickable'
															onClick={() => {
																props.onWorksAlertClick(
																	report
																);
															}}
														>
															<Tooltip title='Possui obras não cadastradas'>
																<ErrorRoundedIcon
																	fontSize='small'
																	color='secondary'
																/>
															</Tooltip>
														</div>
													) : null}
												</TableCell>
												<TableCell
													style={{ width: '10%' }}
												>
													{report.source}
												</TableCell>
												<TableCell
													style={{ width: '10%' }}
												>
													{report.status}
												</TableCell>
												<TableCell
													style={{
														width: '10%',
														textAlign: 'center',
													}}
												>
													{(() => {
														const totalRows = report.total_rows || 0;
														const processedRows = report.processed_rows || 0;
														const progressPercent = report.progress_percent || 0;
														
														if (totalRows > 0) {
															return (
																<Tooltip title={`${processedRows.toLocaleString('pt-BR')} / ${totalRows.toLocaleString('pt-BR')} registros`}>
																	<div>
																		<div>
																			{totalRows.toLocaleString('pt-BR')}
																		</div>
																		{progressPercent > 0 && progressPercent < 100 && (
																			<div style={{ fontSize: '0.75rem', color: '#666' }}>
																				{Number(progressPercent).toFixed(1)}%
																			</div>
																		)}
																	</div>
																</Tooltip>
															);
														} else if (processedRows > 0) {
															return (
																<Tooltip title={`${processedRows.toLocaleString('pt-BR')} registros processados`}>
																	<div>
																		{processedRows.toLocaleString('pt-BR')}
																	</div>
																</Tooltip>
															);
														} else {
															return <span style={{ color: '#999' }}>-</span>;
														}
													})()}
												</TableCell>
												<TableCell
													style={{
														width: '5%',
														textAlign: 'center',
													}}
												>
													{buildRateTag(report)}
												</TableCell>
												<TableCell
													style={{
														width: '20%',
														textAlign: 'center',
													}}
												>
													{report.show_exchange_rate ? (
														<Checkbox
															checked={true}
															onChange={(e) => {
																clickHandler(
																	report.id,
																	!report.show_exchange_rate
																);
															}}
														/>
													) : (
														<Checkbox
															checked={false}
															onChange={(e) => {
																clickHandler(
																	report.id,
																	!report.show_exchange_rate
																);
															}}
														/>
													)}
												</TableCell>
												{props.hasRole([
													'PAYMENT_FILE_WORKS_WRITE',
												]) ? (
													<>
														<TableCell
															className={report.status !== 'Distributed' ? 'clickable' : ''}
															style={{
																width: '10%',
																textAlign:
																	'center',
															}}
															onClick={report.status !== 'Distributed' ? () => {
																props.onDeleteClick(
																	report
																);
															} : undefined}
														>
															{report.status !==
															'Distributed' ? (
																<CancelIcon color='secondary' />
															) : null}
														</TableCell>
													</>
												) : (
													<TableCell></TableCell>
												)}
												{report.status === 'Loaded' ? (
													<TableCell>
														<span
															onClick={() =>
																props.onUpdateStatusClick(
																	report
																)
															}
															className='update-icon'
														>
															<UpdateIcon fontSize='small' />
															Ready
														</span>
													</TableCell>
												) : (
													<TableCell></TableCell>
												)}
											</TableRow>
										);
									}
							  )
							: null}
					</TableBody>
				</Table>
			</TableContainer>
			<Pagination
				shape='rounded'
				className='table-pagination'
				count={props.tableData.pages}
				page={props.tableData.current_page}
				onChange={(e, value) => {
					props.onPageChange(value);
				}}
			/>
		</>
	);
}
